// reCAPTCHA utility service

export class RecaptchaService {
  private static instance: RecaptchaService;
  private isLoaded = false;
  private siteKey: string;

  private constructor() {
    this.siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
  }

  public static getInstance(): RecaptchaService {
    if (!RecaptchaService.instance) {
      RecaptchaService.instance = new RecaptchaService();
    }
    return RecaptchaService.instance;
  }

  /**
   * Load reCAPTCHA script if not already loaded
   */
  public async loadRecaptcha(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isLoaded || window.grecaptcha) {
        this.isLoaded = true;
        resolve();
        return;
      }

      if (!this.siteKey) {
        reject(new Error('reCAPTCHA site key not configured'));
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        window.grecaptcha.ready(() => {
          this.isLoaded = true;
          resolve();
        });
      };

      script.onerror = () => {
        const error = new Error('Failed to load reCAPTCHA script');
        console.error('❌ reCAPTCHA script load error:', error);
        reject(error);
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Generate reCAPTCHA token for a specific action
   */
  public async getToken(action: string = 'pricing_api'): Promise<string> {
    try {
      // Ensure reCAPTCHA is loaded
      if (!this.isLoaded) {
        await this.loadRecaptcha();
      }

      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA not available');
      }

      return await window.grecaptcha.execute(this.siteKey, { action });
    } catch (error) {
      console.error('Error generating reCAPTCHA token:', error);
      // Return empty string as fallback - API should handle missing token gracefully
      return '';
    }
  }

  /**
   * Check if reCAPTCHA is loaded and available
   */
  public isAvailable(): boolean {
    return this.isLoaded && !!window.grecaptcha;
  }
}

// Export singleton instance
export const recaptchaService = RecaptchaService.getInstance();